import { NAV_BY_APP, type NavItem } from "@/lib/appNavigation";

export type FloorTabletHomeSection = {
  moduleKey: "manufacturing" | "shipping" | "warehouse";
  title: string;
  links: NavItem[];
};

/** Floor home shows only these stations, in this order (lower sorts earlier). */
const MANUFACTURING_LINK_ORDER: Record<string, number> = {
  "/manufacturing/kitting": 10,
  "/manufacturing/assembly": 20,
  "/manufacturing/pre-encapsulation": 30,
  "/manufacturing/encapsulation": 35,
  "/manufacturing/final-qc": 40,
  "/manufacturing/qc-incidents": 50,
  "/manufacturing/device-lookup": 60,
};

const SHIPPING_LINK_ORDER: Record<string, number> = {
  "/shipping/arrivals": 10,
  "/shipping/receive": 20,
  "/shipping/holding": 30,
  "/shipping/to-stock": 40,
  "/shipping/pre-pack": 50,
  "/shipping/outbound": 60,
  "/warehouse/recount": 70,
};

const MANUFACTURING_RECOUNT_LINK: NavItem = {
  to: "/warehouse/recount",
  label: "Recount",
  description:
    "Recount SKU on-hand at a warehouse location. Scan a carton to adjust quantity, or carve a new carton label from unclaimed bulk.",
  searchText: "recount inventory cycle count carton unclaimed bulk carve label",
};

function floorLinksForModule(links: NavItem[], order: Record<string, number>): NavItem[] {
  return links
    .filter((link) => order[link.to] != null)
    .sort((left, right) => {
      const leftOrder = order[left.to] ?? 999;
      const rightOrder = order[right.to] ?? 999;
      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }
      return left.label.localeCompare(right.label, undefined, { sensitivity: "base" });
    });
}

export function buildFloorTabletHomeSections(moduleKeys: ReadonlySet<string>): FloorTabletHomeSection[] {
  const sections: FloorTabletHomeSection[] = [];
  if (moduleKeys.has("manufacturing")) {
    const manufacturingLinks = floorLinksForModule(NAV_BY_APP.manufacturing ?? [], MANUFACTURING_LINK_ORDER);
    manufacturingLinks.push(MANUFACTURING_RECOUNT_LINK);
    sections.push({
      moduleKey: "manufacturing",
      title: "Manufacturing",
      links: manufacturingLinks,
    });
  }
  if (moduleKeys.has("shipping")) {
    const shippingLinks = floorLinksForModule(NAV_BY_APP.shipping ?? [], SHIPPING_LINK_ORDER);
    const recountFromWarehouse = (NAV_BY_APP.warehouse ?? []).find((link) => link.to === "/warehouse/recount");
    if (recountFromWarehouse != null && !shippingLinks.some((link) => link.to === "/warehouse/recount")) {
      shippingLinks.push(recountFromWarehouse);
    }
    sections.push({
      moduleKey: "shipping",
      title: "Shipping",
      links: shippingLinks,
    });
  }
  const recountAlreadyShown = sections.some((section) =>
    section.links.some((link) => link.to === "/warehouse/recount"),
  );
  if (moduleKeys.has("warehouse") && !recountAlreadyShown) {
    const recount = (NAV_BY_APP.warehouse ?? []).find((link) => link.to === "/warehouse/recount");
    if (recount != null) {
      sections.push({
        moduleKey: "warehouse",
        title: "Warehouse",
        links: [recount],
      });
    }
  }
  return sections;
}
